import React from 'react';
import Icon from '../../../components/AppIcon';

const ProductionStatisticsPanel = ({ statistics, loading }) => {
  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading statistics...</div>;
  }

  if (!statistics) return null;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Production Statistics</h2>
        <p className="text-sm text-gray-600 mt-1">
          Last 7 days production metrics and efficiency analysis
        </p>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-3">
            <Icon name="Printer" className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded">7 Days</span>
          </div>
          <p className="text-3xl font-bold mb-1">{statistics?.totalPrinted}</p>
          <p className="text-sm opacity-90">Total Cards Printed</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-3">
            <Icon name="CheckCircle" className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded">Success</span>
          </div>
          <p className="text-3xl font-bold mb-1">{statistics?.successfulPrints}</p>
          <p className="text-sm opacity-90">Successful Prints</p>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-3">
            <Icon name="XCircle" className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded">Failed</span>
          </div>
          <p className="text-3xl font-bold mb-1">{statistics?.failedPrints}</p>
          <p className="text-sm opacity-90">Failed Prints</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-3">
            <Icon name="TrendingUp" className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded">Rate</span>
          </div>
          <p className="text-3xl font-bold mb-1">{statistics?.errorRate}%</p>
          <p className="text-sm opacity-90">Error Rate</p>
        </div>
      </div>
      {/* Efficiency Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="Activity" className="w-5 h-5 text-blue-600" />
            Production Efficiency
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Average Per Day</span>
                <span className="text-lg font-bold text-gray-900">{statistics?.averagePerDay}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(parseFloat(statistics?.averagePerDay) / 50) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Success Rate</span>
                <span className="text-lg font-bold text-green-600">
                  {((statistics?.successfulPrints / statistics?.totalPrinted) * 100)?.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all"
                  style={{ width: `${(statistics?.successfulPrints / statistics?.totalPrinted) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="Target" className="w-5 h-5 text-purple-600" />
            Quality Metrics
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Print Quality</span>
              <span className="text-sm font-semibold text-green-600">Excellent</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Color Accuracy</span>
              <span className="text-sm font-semibold text-green-600">98.5%</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Barcode Readability</span>
              <span className="text-sm font-semibold text-green-600">100%</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">Defect Rate</span>
              <span className="text-sm font-semibold text-yellow-600">{statistics?.errorRate}%</span>
            </div>
          </div>
        </div>
      </div>
      {/* Trend Analysis */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" className="w-5 h-5 text-blue-600" />
          Trend Analysis
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ArrowUp" className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-900">Production Volume</span>
            </div>
            <p className="text-2xl font-bold text-green-600">+12%</p>
            <p className="text-xs text-gray-600 mt-1">vs. previous week</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ArrowDown" className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-gray-900">Error Rate</span>
            </div>
            <p className="text-2xl font-bold text-green-600">-3%</p>
            <p className="text-xs text-gray-600 mt-1">vs. previous week</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ArrowUp" className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-900">Efficiency</span>
            </div>
            <p className="text-2xl font-bold text-green-600">+8%</p>
            <p className="text-xs text-gray-600 mt-1">vs. previous week</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionStatisticsPanel;
